import React, { useRef, useState } from 'react';

import { Container, ErrorText, Label, Tag, TagInput, TagLabel, TagRemove, TagsWrapper } from './MultiTagInput.styles';

interface MultiTagInputProps {
  tags?: string[];
  onChange: (tags: string[]) => void;
  onRemove: (tags: string[]) => void;
  error?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}

const MultiTagInput = ({
  tags,
  onChange,
  onRemove,
  error,
  label,
  disabled,
  required,
  placeholder,
}: MultiTagInputProps) => {
  const safeTags = tags ?? [];
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      onChange([...safeTags, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && safeTags.length > 0) {
      onRemove(safeTags.slice(0, -1));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes(',')) {
      const parts = value.split(',');
      const remaining = parts.pop() ?? '';
      parts.forEach((part) => addTag(part));
      setInputValue(remaining);
    } else {
      setInputValue(value);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  return (
    <Container $disabled={Boolean(disabled)}>
      <Label>{required ? `${label} *` : label}</Label>
      <TagsWrapper
        $error={Boolean(error)}
        $disabled={Boolean(disabled)}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {safeTags.map((tag, index) => (
          <Tag key={index}>
            <TagLabel>{tag}</TagLabel>
            {!disabled && <TagRemove onClick={() => onRemove(safeTags.filter((t) => t !== tag))}>×</TagRemove>}
          </Tag>
        ))}
        <TagInput
          ref={inputRef}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={safeTags.length === 0 ? placeholder : undefined}
        />
      </TagsWrapper>
      <ErrorText>{error}</ErrorText>
    </Container>
  );
};

export default MultiTagInput;
